/**
 * LUKHAS.ID Secure Authentication Client
 *
 * Security improvements over localStorage-based auth:
 * - Access tokens stored in memory only (not localStorage)
 * - Refresh tokens stored in HttpOnly cookies (server-side)
 * - Automatic token refresh with race-condition prevention
 * - Refresh token rotation with reuse detection
 *
 * The server must:
 * - Set refresh token as HttpOnly, Secure, SameSite=Lax cookie
 * - Rotate refresh tokens on each use
 * - Detect and invalidate all tokens on reuse attempts
 */

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL || 'https://api.lukhas.id/v1'

// In-memory token storage (not persisted, cleared on page reload)
let accessToken: string | null = null
let accessTokenExpiry = 0

// Prevent concurrent refresh attempts
let refreshing = false
let refreshPromise: Promise<void> | null = null

// Event listeners for auth state changes
type AuthEventListener = (isAuthenticated: boolean) => void
const authListeners: Set<AuthEventListener> = new Set()

/**
 * Set access token in memory with expiry tracking
 * Called after successful auth (login, refresh, etc.)
 */
export function setAccessToken(token: string, expiresInSec: number): void {
  accessToken = token
  // Subtract 5 seconds to refresh slightly before expiry
  accessTokenExpiry = Date.now() + expiresInSec * 1000 - 5000
  notifyListeners(true)
}

/**
 * Clear all auth state (memory tokens, triggers server cookie clear)
 */
export function clearAuth(): void {
  accessToken = null
  accessTokenExpiry = 0
  notifyListeners(false)
}

/**
 * Check if we have a valid access token
 */
export function hasValidToken(): boolean {
  return accessToken !== null && Date.now() < accessTokenExpiry
}

/**
 * Check if access token is expired or about to expire
 */
export function isTokenExpired(): boolean {
  return !accessToken || Date.now() >= accessTokenExpiry
}

/**
 * Subscribe to auth state changes
 */
export function onAuthChange(listener: AuthEventListener): () => void {
  authListeners.add(listener)
  return () => authListeners.delete(listener)
}

/**
 * Notify all listeners of auth state change
 */
function notifyListeners(isAuthenticated: boolean): void {
  authListeners.forEach(listener => listener(isAuthenticated))
}

/**
 * Refresh access token using HttpOnly cookie
 * Uses single-flight pattern to prevent concurrent refresh calls
 */
async function doRefresh(): Promise<void> {
  // Return existing promise if refresh is in progress
  if (refreshPromise) return refreshPromise

  refreshing = true
  refreshPromise = (async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Send HttpOnly cookie
        headers: { 'Content-Type': 'application/json' },
      })

      if (!resp.ok) {
        // Refresh failed - clear auth state
        // Common causes: expired refresh token, token reuse detected
        const error = await resp.json().catch(() => ({ code: 'REFRESH_FAILED' }))

        // Check for token reuse detection (security event)
        if (error.code === 'TOKEN_REUSE_DETECTED') {
          console.error('[Auth] Token reuse detected - all sessions invalidated')
        }

        clearAuth()
        throw new Error(error.message || 'refresh_failed')
      }

      const body = await resp.json()
      setAccessToken(body.accessToken, body.expiresIn)

      // Server should have set new rotated refresh token in HttpOnly cookie
    } finally {
      refreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * Authenticated fetch wrapper
 * - Automatically adds Authorization header
 * - Refreshes expired tokens before requests
 * - Retries on 401 after refresh
 *
 * @param input - URL or Request object
 * @param init - Fetch options
 * @param retry - Whether to retry on 401 (default: true)
 */
export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  retry = true
): Promise<Response> {
  // Proactively refresh if token is expired
  if (accessToken && Date.now() > accessTokenExpiry) {
    await doRefresh()
  }

  // Build headers with Authorization
  const headers = new Headers(init?.headers)
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  // Make request with credentials for cookie handling
  const resp = await fetch(input, {
    ...init,
    headers,
    credentials: 'include', // Always include credentials for cookie handling
  })

  // Handle 401 Unauthorized
  if (resp.status === 401 && retry) {
    try {
      await doRefresh()

      // Retry request with new token
      const retryHeaders = new Headers(init?.headers)
      if (accessToken) {
        retryHeaders.set('Authorization', `Bearer ${accessToken}`)
      }

      return fetch(input, {
        ...init,
        headers: retryHeaders,
        credentials: 'include',
      })
    } catch (e) {
      // Refresh failed - clear auth and rethrow
      clearAuth()
      throw e
    }
  }

  return resp
}

/**
 * Authenticated JSON request helper
 * Combines authFetch with JSON parsing
 */
export async function authRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`

  const resp = await authFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  if (!resp.ok) {
    const error = await resp.json().catch(() => ({
      message: resp.statusText
    }))
    throw new Error(error.message || `API Error: ${resp.status}`)
  }

  return resp.json()
}

/**
 * Initialize auth state from server session
 * Called on app startup to check if user has valid session
 */
export async function initializeAuth(): Promise<boolean> {
  try {
    // Try to refresh - if HttpOnly cookie is valid, we'll get new access token
    await doRefresh()
    return true
  } catch {
    // No valid session
    return false
  }
}

/**
 * Logout - clear local state and server session
 */
export async function logout(): Promise<void> {
  try {
    await authFetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
    })
  } finally {
    clearAuth()
  }
}

// Export for testing/debugging
export const __internal = {
  getAccessToken: () => accessToken,
  getExpiry: () => accessTokenExpiry,
  isRefreshing: () => refreshing,
}
